/**
 * Google Analytics 4 helper for Memory Lane Care.
 *
 * The base gtag.js snippet is loaded from /public/index.html with
 * Measurement ID G-P2WVZEFYYP. This module is the only place that should
 * touch `window.gtag` — everywhere else calls `track(EVENTS.X, props)`
 * so we keep event names typo-free and easy to refactor later.
 *
 * Consent: at boot (/src/index.js → applyDefaultConsent), GA4 is put into
 * Consent Mode v2 with analytics_storage denied. When the caregiver opts
 * in via the consent banner, /src/lib/consent.js calls
 * gtag('consent','update',{analytics_storage:'granted'}) and from then
 * on cookies are set. We still queue events into dataLayer either way so
 * that nothing is lost the instant consent flips on.
 *
 * Internal traffic: /lib/internalTraffic.js's `shouldDropEvent()` is
 * checked at the top of every event helper here. If the visitor is on
 * a preview/localhost host, or signed in with an internal email /
 * admin role, the event becomes a silent no-op and never reaches GA4.
 *
 * If gtag is missing (ad-blocker, DNT, local dev without network), every
 * call is a silent no-op — analytics should never break the app.
 *
 * Every track() call auto-enriches the payload with a `user_context`
 * snapshot (user_type, dementia_stage, caregiver_relationship, country,
 * session_id, page_name) that we cache locally to keep network noise low.
 */

import { shouldDropEvent } from "@/lib/internalTraffic";

export const GA_MEASUREMENT_ID = "G-P2WVZEFYYP";

// ---- Event catalog ---------------------------------------------------------
// Use these constants everywhere instead of raw strings — typos become
// compile-time references that fail loudly.
export const EVENTS = {
    // Acquisition & auth
    ACCOUNT_CREATED: "account_created",          // legacy custom event
    SIGN_UP: "sign_up",                          // GA4 recommended event (KEY EVENT)
    CAREGIVER_SIGNUP: "caregiver_signup",        // KEY EVENT — Google Ads conversion import
    LOGIN: "login",

    // Homepage activation experiences (pre-signup demos on the landing)
    ACTIVATION_CARD_CLICK: "activation_card_click",
    ACTIVATION_STEP: "activation_step",
    ACTIVATION_COMPLETED: "activation_completed",
    ACTIVATION_SIGNUP_CLICK: "activation_signup_click",

    // Discovery
    SEARCH_MEMORY_LANE: "search_memory_lane",
    RESOURCE_CLICK: "resource_click",
    RESOURCE_SAVED: "resource_saved",
    RESOURCE_SHARED: "resource_shared",
    RESOURCE_COMPLETED: "resource_completed",
    FIND_LOCAL_RESOURCES: "find_local_resources",

    // Personalized Home nudges ("Lane noticed")
    NUDGE_IMPRESSION: "nudge_impression",
    NUDGE_CLICK: "nudge_click",

    // Caregiver Journey
    ROADMAP_VIEWED: "roadmap_viewed",
    ROADMAP_STARTED: "roadmap_started",              // legacy alias
    ROADMAP_STEP_COMPLETED: "roadmap_step_completed",
    ROADMAP_COMPLETED: "roadmap_completed",          // legacy alias (stage-complete)
    BURNOUT_ASSESSMENT_STARTED: "burnout_assessment_started",
    BURNOUT_ASSESSMENT_COMPLETED: "burnout_assessment_completed",
    BEHAVIOR_LOG_ADDED: "behavior_log_created",      // funnel: Behavior Log Created (wired in Behavior.jsx)
    QUESTION_SAVED: "question_saved",                // appointment question saved
    CARE_SUMMARY_GENERATED: "care_summary_generated",
    CARE_SUMMARY_SHARE_STARTED: "care_summary_share_started",
    CARE_SUMMARY_SHARED: "care_summary_shared",// KEY EVENT (conversion)
    FAMILY_PROFILE_COMPLETED: "family_profile_completed",
    CARE_PROVIDER_ADDED: "care_provider_added",          // My Care Team: provider plugged in
    CARE_PROVIDER_ENDED: "care_provider_ended",          // My Care Team: merge-off (transition)

    // Lane AI
    ASK_LANE_STARTED: "ask_lane_started",
    ASK_LANE_CONVERSATION: "ask_lane_conversation",  // per user message
    CONVERSATION_COMPLETED: "conversation_completed",// when Lane finishes a reply
    LANE_RECOMMENDATION_CLICKED: "lane_recommendation_clicked",
    LANE_FOLLOWUP_COMPLETED: "lane_followup_completed",

    // GUIDE program
    GUIDE_ELIGIBILITY_STARTED: "guide_eligibility_started",
    GUIDE_ELIGIBILITY_COMPLETED: "guide_eligibility_completed", // KEY EVENT (conversion)
    GUIDE_PROVIDER_SELECTED: "guide_provider_selected",
    GUIDE_PACKET_SHARED: "guide_packet_shared",

    // Appointment prep
    APPOINTMENT_PREP_OPENED: "appointment_prep_opened",
    APPOINTMENT_PREP_STARTED: "appointment_prep_started", // legacy alias
    APPOINTMENT_PREP_COMPLETED: "appointment_prep_completed",
    APPOINTMENT_SUMMARY_SHARED: "appointment_summary_shared",
    APPOINTMENT_SUMMARY_DOWNLOADED: "appointment_summary_downloaded",

    // Engagement
    DAILY_CHECK_IN_COMPLETED: "daily_check_in_completed",
    FIRST_SESSION_CHECKIN: "first_session_checkin",
    CAREGIVER_CHECKIN_FIRST_VISIT: "caregiver_checkin_first_visit",
    CAREGIVER_CHECKIN_RETURNING: "caregiver_checkin_returning",
    WEEKLY_CHECK_IN_COMPLETED: "weekly_check_in_completed",
    RETURN_VISIT_1_DAY: "return_visit_day_1",
    RETURN_VISIT_7_DAYS: "return_visit_7_days",
    RETURN_VISIT_30_DAYS: "return_visit_30_days",
    // Funnel steps (Landing → CTA → Signup started → Signup completed →
    // Onboarding → First AI conversation → Behavior log → Return visits)
    CTA_CLICK: "cta_click",
    SIGNUP_STARTED: "signup_started",
    ONBOARDING_STARTED: "onboarding_started",
    ONBOARDING_COMPLETED: "onboarding_completed",
    // Canonical caregiver-activity events (auto-fired as aliases below —
    // these are the names the founder funnels and Key Events are built on).
    LANE_CHAT_STARTED: "lane_chat_started",
    CHECK_IN_COMPLETED: "check_in_completed",
    CAREGIVER_TOOL_USED: "caregiver_tool_used",
    NAVIGATOR_BOOKING_STARTED: "navigator_booking_started",
    NAVIGATOR_BOOKING_COMPLETED: "navigator_booking_completed",
    FIRST_AI_CONVERSATION: "first_ai_conversation",

    // Privacy / consent (MHMDA-style storage-consent checkbox on signup)
    MEMORY_CONSENT_SHOWN: "memory_consent_shown",
    MEMORY_CONSENT_AGREED: "memory_consent_agreed",

    // Monetization
    CHECKOUT_STARTED: "checkout_started",
    FREE_TRIAL_STARTED: "free_trial_started",
    SUBSCRIPTION_STARTED: "subscription_started",    // KEY EVENT (conversion)
    SUBSCRIPTION_RENEWED: "subscription_renewed",
    SUBSCRIPTION_CANCELLED: "subscription_cancelled",
    CALENDLY_BOOKING: "calendly_booking",            // KEY EVENT (conversion)
    PURCHASE: "purchase",                            // KEY EVENT (conversion, GA4 standard)

    // Care navigator (legacy alias for calendly_booking)
    CARE_NAVIGATOR_SESSION_BOOKED: "care_navigator_session_booked",

    // Feedback & partner pipeline
    FEEDBACK_SUBMITTED: "feedback_submitted",
    PARTNER_REFERRAL: "partner_referral",

    // Loved one's Journey (care records)
    CARE_RECORD_UPLOADED: "care_record_uploaded",
    CARE_RECORD_FACT_APPROVED: "care_record_fact_approved",
};

// ---- Reddit Pixel conversions ----------------------------------------------
// Pixel a2_jgeyf91srxen loads from /public/index.html. Two conversions:
//   • SignUp     — fired inside trackSignUp() (same idempotency guard).
//   • Activation — custom event, fired ONCE per caregiver the first time
//     any event in RDT_ACTIVATION_EVENTS passes through track(). Does not
//     depend on onboarding; add future meaningful events to the set below.
// Both inherit shouldDropEvent() (internal/preview traffic never converts).
const RDT_ACTIVATION_EVENTS = new Set([
    "ask_lane_conversation", "first_ai_conversation",           // asked Lane
    "daily_check_in_completed", "weekly_check_in_completed",    // wellness check-ins (server-confirmed submits only)
    "burnout_assessment_completed",
    "behavior_log_created",                                     // logged a behavior
    "question_saved",                                           // appointment question
    "care_record_uploaded", "care_record_fact_approved",        // Journey entries
    "care_provider_added",
    // Deliberately EXCLUDED (founder pre-launch review, Jun 2026):
    //   care_summary_generated — fires on page LOAD (opened ≠ action)
    //   first_session_checkin  — fires pre-server-confirm on the post-signup
    //     dashboard (intake-adjacent); its successful submit still activates
    //     via daily_check_in_completed.
]);

function rdtSafe(...args) {
    try {
        if (typeof window !== "undefined" && typeof window.rdt === "function") window.rdt(...args);
    } catch { /* ads must never break the app */ }
}

function rdtActivationOnce() {
    try {
        let uid = "";
        try { uid = window.localStorage.getItem("mlcn_session_id") || ""; } catch { /* noop */ }
        const guard = `ml_rdt_activation_${uid || "anon"}`;
        if (window.localStorage.getItem(guard)) return;
        window.localStorage.setItem(guard, String(Date.now()));
        rdtSafe("track", "Custom", { customEventName: "Activation" });
    } catch { /* noop */ }
}

// ---- URL privacy redaction --------------------------------------------------
// Share links and magic links carry capability tokens in the path; some
// routes carry record ids. Nothing token- or id-like may reach GA / Clarity.
// Query strings are dropped entirely (magic-link codes live there).
export function redactedPath() {
    try {
        let p = window.location.pathname || "/";
        p = p.replace(/(\/(?:shared-update|passport\/share|patient-profile\/share|share\/care-team|share\/guide-screeners|share|magic|verify)\/)[^/?#]+/g, "$1:token");
        // Short capability-token routes: /s/<care-summary>, /r/<report>,
        // /join/<invite-code>, /tester/<access-code>. Codes can be under
        // 22 chars so the generic long-token rule below won't catch them.
        p = p.replace(/(\/(?:s|r|join|tester)\/)[^/?#]+/g, "$1:token");
        p = p.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, "/:id");
        // Long high-entropy segments become :token — but NOT readable article
        // slugs like /blog/10-things-nobody-tells-you (all-lowercase words
        // joined by 2+ hyphens). This was sending /blog/:token to GA4 instead
        // of the real article URL.
        p = p.replace(/\/([A-Za-z0-9_-]{22,})(?=\/|$)/g, (m, seg) =>
            /^[a-z0-9]+(?:-[a-z0-9]+){2,}$/.test(seg) ? m : "/:token");
        return p === "" ? "/" : p;
    } catch { return "/"; }
}

function redactedLocation() {
    try { return window.location.origin + redactedPath(); } catch { return ""; }
}

// Events the user has asked GA4 to mark as Key Events (conversions). The
// "mark as Key Event" toggle lives in the GA4 Admin UI (Admin → Events →
// toggle "Mark as key event"). This list is the source of truth so a
// future agent doesn't rename them by accident.
export const KEY_EVENTS = [
    EVENTS.SIGN_UP,                  // GA4 recommended sign-up event
    EVENTS.CAREGIVER_SIGNUP,         // Google Ads conversion (imported from GA4)
    EVENTS.ACCOUNT_CREATED,          // legacy alias
    EVENTS.GUIDE_ELIGIBILITY_COMPLETED,
    EVENTS.CARE_SUMMARY_GENERATED,
    EVENTS.CALENDLY_BOOKING,
    EVENTS.SUBSCRIPTION_STARTED,
    EVENTS.PURCHASE,
];
// Back-compat alias — older code imports CONVERSION_EVENTS.
export const CONVERSION_EVENTS = KEY_EVENTS;

// ---- User context cache ----------------------------------------------------
// We auto-merge a small "who is this caregiver" snapshot into every
// event so dimensions like dementia_stage and caregiver_relationship are
// available without configuring custom dimensions in GA4 first.
//
// The cache is updated by the auth provider (login/logout/refresh) and
// by the intake/profile pages when a caregiver answers stage / relation
// questions. Anonymous visitors get user_type=guest with no PII.
const CONTEXT_KEY = "mlcn_ga_user_context";

function readContext() {
    try {
        if (typeof window === "undefined") return {};
        const raw = window.localStorage.getItem(CONTEXT_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function writeContext(ctx) {
    try {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(CONTEXT_KEY, JSON.stringify(ctx || {}));
    } catch {
        /* quota / disabled storage — silent */
    }
}

/**
 * Update the cached user-context fields. Pass a partial patch — anything
 * you omit keeps its previous value. Pass `null` to clear everything
 * (used by logout).
 *
 * Recognized fields:
 *   user_type             "guest" | "registered" | "subscriber" | "admin"
 *   dementia_stage        "early" | "middle" | "late" | "unsure" | FAST 1-7
 *   caregiver_relationship  "spouse" | "child" | "parent" | "sibling" | ...
 *   country               2-letter ISO code from useCountry()
 *   session_id            local session id from getSessionId()
 *   subscription_plan     plan code if subscribed
 */
export function setUserContext(patch) {
    if (patch === null) {
        writeContext({});
        return;
    }
    const next = { ...readContext(), ...(patch || {}) };
    writeContext(next);
}

export function getUserContext() {
    return readContext();
}

/**
 * Fire a GA4 event. Safe to call before gtag has loaded, before consent,
 * or in environments where gtag was blocked — it becomes a no-op.
 *
 * Auto-merges the cached user_context, `page_name` (from document.title),
 * and `page_location` / `page_path` so every event lands in GA4 with
 * useful dimensions, no manual plumbing per callsite.
 *
 * @param {string} eventName  one of EVENTS.*
 * @param {object} [params]   additional event parameters
 */
// Canonical caregiver-activity aliases. When the legacy event fires, the
// canonical event fires too (same params) so GA4 funnels/Key Events can be
// built on stable names without breaking historical events.
function canonicalAliasFor(eventName, params) {
    switch (eventName) {
        case EVENTS.ASK_LANE_STARTED: return EVENTS.LANE_CHAT_STARTED;
        case EVENTS.DAILY_CHECK_IN_COMPLETED:
        case EVENTS.WEEKLY_CHECK_IN_COMPLETED: return EVENTS.CHECK_IN_COMPLETED;
        case "caregiver_tool_download":
        case "caregiver_tool_digital_cta_click": return EVENTS.CAREGIVER_TOOL_USED;
        case EVENTS.CALENDLY_BOOKING:
            return (params && params.stage === "intent")
                ? EVENTS.NAVIGATOR_BOOKING_STARTED
                : EVENTS.NAVIGATOR_BOOKING_COMPLETED;
        default: return null;
    }
}

export function track(eventName, params = {}) {
    if (typeof window === "undefined") return;
    // Drop all internal traffic (admin, @emergent.sh, preview/localhost
    // hosts, manual opt-out) before the event leaves the browser.
    if (shouldDropEvent()) return;
    try {
        const ctx = readContext();
        let debugMode = false;
        try { debugMode = window.localStorage.getItem("ml_analytics_debug") === "1"; } catch { /* noop */ }
        const payload = {
            page_location: redactedLocation(),
            page_path: redactedPath(),
            page_name: (document?.title || "").slice(0, 80),
            user_type: ctx.user_type || "guest",
            // DebugView verification mode — events show in GA4 DebugView and
            // are tagged so they can be filtered out of production reports.
            ...(debugMode ? { debug_mode: true, traffic_type: "internal_debug" } : {}),
            // Spread caregiver-context fields if known. GA4 will auto-register
            // them as event parameters; the user creates custom dimensions
            // in GA4 admin to surface them in reports.
            ...(ctx.dementia_stage ? { dementia_stage: ctx.dementia_stage } : {}),
            ...(ctx.caregiver_relationship ? { caregiver_relationship: ctx.caregiver_relationship } : {}),
            ...(ctx.country ? { country: ctx.country } : {}),
            ...(ctx.subscription_plan ? { subscription_plan: ctx.subscription_plan } : {}),
            // Research-panel participants (Prolific / User Interviews) — kept
            // out of real-caregiver reports via a GA4 data filter on this param.
            ...(!debugMode && ctx.traffic_type ? { traffic_type: ctx.traffic_type } : {}),
            ...params,
        };
        if (typeof window.gtag === "function") {
            window.gtag("event", eventName, payload);
        } else {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: eventName, ...payload });
        }
        // Dual-fire the canonical caregiver-activity alias (see map above).
        const alias = canonicalAliasFor(eventName, params);
        if (alias) {
            if (typeof window.gtag === "function") window.gtag("event", alias, payload);
            else window.dataLayer.push({ event: alias, ...payload });
        }
        // Mirror the event into Clarity as a timeline marker so session
        // recordings show what the user was doing at every step. Same
        // internal-traffic filter applies (already handled by
        // shouldDropEvent above). Non-blocking, silently no-ops if
        // Clarity hasn't loaded yet.
        try {
            if (typeof window.clarity === "function") {
                window.clarity("event", String(eventName));
            }
        } catch { /* Clarity must never throw */ }
        // Reddit Activation conversion — first meaningful action, once ever.
        if (RDT_ACTIVATION_EVENTS.has(String(eventName))) rdtActivationOnce();
    } catch {
        // Analytics must never throw into the host app.
    }
}

/**
 * Fire an event at most once per browser session for a given guard key.
 * Used for events that have a "started/opened" semantic — we don't want
 * tab toggling to inflate the count.
 */
export function trackOnce(guardKey, eventName, params = {}) {
    if (typeof window === "undefined") return;
    if (shouldDropEvent()) return;
    const skey = `ml_ga_once_${guardKey}`;
    try {
        if (window.sessionStorage.getItem(skey)) return;
        window.sessionStorage.setItem(skey, "1");
    } catch {
        /* private mode — fall through and fire */
    }
    track(eventName, params);
}

/**
 * Fire the GA4 sign-up event correctly — once per account.
 *
 * GA4 only marks the recommended `sign_up` event as a conversion. We
 * were previously emitting only `account_created` (custom) which is why
 * Founder Dashboard reported 278 signups but GA4 reported 0 conversions.
 *
 * This helper:
 *   1. Sets the user_id (so subsequent events attribute correctly)
 *   2. Emits BOTH `sign_up` (GA4 recommended, marked as Key Event in admin)
 *      AND `account_created` (legacy custom event, keeps old reports working)
 *   3. Uses localStorage to guarantee we only fire once per user, ever —
 *      even if the user double-clicks Submit or the registration flow
 *      re-runs after a network blip.
 *
 * Call this from EVERY signup path (Landing inline form, /beta, /login
 * register tab, partner access-code flow, auth provider).
 */
/**
 * Return-visit funnel markers — D1 / D7 / D30 after account creation.
 * Fired at most once per user per milestone (localStorage guard), from
 * auth refresh/login. Internal users are dropped inside track().
 */
export function trackReturnVisit(user) {
    try {
        if (!user || user === false || !user.created_at) return;
        const days = Math.floor((Date.now() - new Date(user.created_at).getTime()) / 86400000);
        if (days < 1) return;
        const uid = user.id || "u";
        // Day-accurate windows — return_visit_day_1 must mean "came back on
        // day 1", not "came back eventually". (Audit fix, Jul 2026.)
        const marks = [
            [1, 1, EVENTS.RETURN_VISIT_1_DAY],
            [7, 13, EVENTS.RETURN_VISIT_7_DAYS],
            [30, 44, EVENTS.RETURN_VISIT_30_DAYS],
        ];
        for (const [lo, hi, ev] of marks) {
            if (days >= lo && days <= hi) {
                const k = `mlcn_rv_${lo}_${uid}`;
                if (!window.localStorage.getItem(k)) {
                    window.localStorage.setItem(k, "1");
                    track(ev, { days_since_signup: days });
                }
            }
        }
    } catch { /* storage disabled — skip */ }
}

export function trackSignUp(user, params = {}) {
    if (typeof window === "undefined") return;
    // Internal signups (admin seeding, teammates testing the flow) must
    // not count toward the sign_up Key Event in GA4 reports.
    if (shouldDropEvent()) return;
    try {
        const uid = user?.id || user?.user_id || params.user_id;
        // Idempotency: keyed on user id where we have one, else on email
        // hash, else on a session-scoped flag.
        const guard = uid
            ? `ml_ga_signup_uid_${uid}`
            : (user?.email ? `ml_ga_signup_email_${user.email}` : "ml_ga_signup_session");
        if (window.localStorage.getItem(guard)) return;
        window.localStorage.setItem(guard, String(Date.now()));

        if (uid) identify(uid);
        // Activation attribution — which homepage demo (if any) led here.
        let act = null;
        try { act = JSON.parse(window.localStorage.getItem("ml_activation") || "null"); } catch { /* ignore */ }
        const payload = {
            method: params.method || "email",
            ...(user?.country ? { country: user.country } : {}),
            ...(user?.role ? { role: user.role } : {}),
            ...(act?.first_card ? {
                activation_first_card: act.first_card,
                activation_last_card: act.last_card || act.first_card,
                activation_completed: (act.completed || []).join(",") || "none",
            } : {}),
            ...params,
        };
        // GA4 recommended event — this is the one that gets credit for
        // conversions in the Key Events report.
        track(EVENTS.SIGN_UP, payload);
        // Google Ads conversion event. Fired ONLY here — trackSignUp is
        // called exclusively after the backend confirms a successful
        // registration (never on validation errors or failed requests),
        // and the localStorage guard above makes it once-per-account.
        track(EVENTS.CAREGIVER_SIGNUP, payload);
        // Legacy custom event — kept so older dashboards / explorations
        // don't lose their data series.
        track(EVENTS.ACCOUNT_CREATED, payload);
        // Reddit Ads conversion — same idempotency guard as above.
        rdtSafe("track", "SignUp");
        // Caregiver Tools attribution — if this signup started from a "30
        // Days" tool page (download or digital CTA), credit the tool.
        try {
            const attr = JSON.parse(localStorage.getItem("ml_tool_attr") || "null");
            if (attr && attr.tool_name) {
                track("caregiver_tool_signup", {
                    tool_day: attr.tool_day, tool_name: attr.tool_name, ...params,
                });
                localStorage.removeItem("ml_tool_attr");
            }
        } catch { /* noop */ }
    } catch {
        /* analytics must never throw */
    }
}

/**
 * Fire a GA4 `page_view` event with explicit, non-empty parameters.
 *
 * This replaces the gtag.js `send_page_view: true` auto-pageview which
 * fired BEFORE React mounted (so document.title was the static
 * index.html title and the URL was correct but document.title was wrong
 * for SPA routes that hadn't rendered yet). Also handles SPA route
 * changes — gtag.js doesn't refire pageviews on history.pushState.
 *
 * Always provides:
 *   • page_location  — the full URL (window.location.href)
 *   • page_path      — the route path, defaulting "" → "/"
 *   • page_title     — current document.title (after React updates it)
 *
 * Pass `extra` for any additional context (e.g. article_slug, group).
 */
export function trackPageView(extra = {}) {
    if (typeof window === "undefined") return;
    if (shouldDropEvent()) return;
    try {
        const path = window.location.pathname || "/";
        // Friendly section names for the landing + a few high-traffic
        // routes so GA4 reports show a human label instead of just "/".
        // SeoHead updates document.title on most internal routes, but
        // for the landing page it stays as the static index.html title.
        const fallbackTitle = (
            path === "/" ? "Memory Lane — Home"
                : path.startsWith("/guide") ? "Memory Lane — GUIDE program"
                    : path.startsWith("/admin") ? "Memory Lane — Admin"
                        : path.startsWith("/library") ? "Memory Lane — Knowledge Library"
                            : path.startsWith("/partners") ? "Memory Lane — Partners"
                                : "Memory Lane Care Navigator"
        );
        const rawTitle = (document?.title || "").trim();
        // If React/SeoHead has set a meaningful title, prefer it.
        // Otherwise fall back to the path-based label so GA4 never
        // receives a blank page_title (the root cause of "blank
        // landing" rows showing up in GA4 reports).
        const title = (rawTitle && rawTitle !== "Memory Lane Care Navigator")
            ? rawTitle
            : fallbackTitle;
        const payload = {
            page_location: redactedLocation(),
            // Critical: "" → "/" so the homepage doesn't land in GA4 with
            // an empty page_path. This was the root cause of the empty-
            // page_path issue we saw on memorylanecare.net.
            page_path: redactedPath(),
            page_title: title.slice(0, 120),
            ...extra,
        };
        if (typeof window.gtag === "function") {
            window.gtag("event", "page_view", payload);
        } else {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: "page_view", ...payload });
        }
    } catch {
        /* analytics must never throw */
    }
}

/**
 * Identify the current user so GA4 reports can attribute events to a
 * stable user_id (separate from Google's client_id cookie). Pass `null`
 * to clear on logout.
 */
export function identify(userId) {
    if (typeof window === "undefined") return;
    if (shouldDropEvent()) return;
    try {
        if (typeof window.gtag === "function") {
            window.gtag("config", GA_MEASUREMENT_ID, {
                user_id: userId || undefined,
            });
        }
    } catch {
        /* no-op */
    }
}

/* Canonical event taxonomy (Founder OS, Jul 2026). ALIAS LAYER ONLY —
   legacy names above keep firing unchanged so historical GA4 trends stay
   intact. Mirrors EVENT_TAXONOMY in backend/routes/founder_os.py. */
export const EVENT_TAXONOMY = {
    account: { account_created: ["sign_up", "account_created"], onboarding_completed: ["intake_completed"] },
    loved_one: { loved_one_added: ["intake_completed", "patient_profile_created"], patient_updated: ["patient_profile_updated"] },
    lane: { conversation_started: ["ask_lane_started", "chat_started"], conversation_completed: ["chat_message_sent"], memory_referenced: ["lane_memory_used"] },
    behavior: { behavior_logged: ["behavior_logged"], behavior_guidance_viewed: ["guidance_viewed", "behavior_guidance_viewed"] },
    resources: { resource_opened: ["resource_click", "find_local_resources"], resource_saved: ["wellness_resource_saved"], resource_shared: ["guide_packet_shared"] },
    appointments: { summary_generated: ["care_summary_generated", "appointment_prep_completed"], summary_shared: ["appointment_summary_shared"], summary_printed: ["summary_downloaded"] },
    organization: { provider_connected: ["provider_added"], organization_joined: ["org_connected"], guide_completed: ["roadmap_completed"] },
};

